import { Badge, Button, Card, Modal, Stack, Table, Text, TextInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { api, unwrap } from '../api.ts';
import { Failure, Loading, Page, useMe, useSave } from '../ui.tsx';

function NewOrganization(props: { codes: string[]; onDone: () => void }) {
  const next = Math.max(0, ...props.codes.map((code) => Number(code.slice(1))).filter(Number.isFinite)) + 1;
  const [form, setForm] = useState({
    code: `T${String(next).padStart(4, '0')}`,
    name: '',
    idpOrganizationId: '',
  });
  const create = useSave({
    run: () =>
      unwrap(
        api().POST('/v1/organizations', {
          body: {
            code: form.code,
            name: form.name,
            ...(form.idpOrganizationId ? { idpOrganizationId: form.idpOrganizationId } : {}),
          },
        }),
      ),
    invalidates: [['organizations']],
    success: (organization) =>
      `${organization.name} onboarded; add its first admin from Users or the users CLI`,
    onDone: props.onDone,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(undefined);
  };
  return (
    <form onSubmit={submit}>
      <Stack>
        <TextInput
          label="Code"
          required
          value={form.code}
          onChange={(event) => setForm({ ...form, code: event.currentTarget.value.toUpperCase() })}
        />
        <TextInput
          label="Name"
          required
          data-autofocus
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.currentTarget.value })}
        />
        <TextInput
          label="Zitadel organization id"
          description="People from this identity-provider organization sign in to it"
          value={form.idpOrganizationId}
          onChange={(event) => setForm({ ...form, idpOrganizationId: event.currentTarget.value })}
        />
        <Failure error={create.error} />
        <Button type="submit" loading={create.isPending}>
          Onboard organization
        </Button>
      </Stack>
    </form>
  );
}

export function OrganizationsPage() {
  const me = useMe();
  const organizations = useQuery({
    queryKey: ['organizations'],
    queryFn: () => unwrap(api().GET('/v1/organizations')),
  });
  const [creating, create] = useDisclosure(false);
  return (
    <Page
      title="Organizations"
      description="Landlords and tenants on the platform. Only Budovan staff onboard organizations."
      actions={
        me.data?.isOperator ? (
          <Button leftSection={<IconPlus size={16} />} onClick={create.open}>
            Onboard organization
          </Button>
        ) : null
      }
    >
      <Card withBorder padding="lg">
        {organizations.isPending ? <Loading /> : null}
        <Failure error={organizations.error} />
        {organizations.data ? (
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Code</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Identity provider</Table.Th>
                  <Table.Th>Language</Table.Th>
                  <Table.Th>Modules</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {organizations.data.map((organization) => (
                  <Table.Tr key={organization.id}>
                    <Table.Td ff="monospace">{organization.code}</Table.Td>
                    <Table.Td fw={600}>
                      {organization.name}{' '}
                      {organization.id === me.data?.organizationId ? (
                        <Badge size="xs" variant="light" color="ink">
                          You
                        </Badge>
                      ) : null}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={organization.idpOrganizationId ? undefined : 'dimmed'}>
                        {organization.idpOrganizationId ?? 'not linked'}
                      </Text>
                    </Table.Td>
                    <Table.Td>{organization.defaultLanguage}</Table.Td>
                    <Table.Td>
                      {organization.modules.length === 0 ? '—' : organization.modules.join(', ')}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : null}
      </Card>
      <Modal opened={creating} onClose={create.close} title="Onboard organization" centered>
        <NewOrganization
          codes={organizations.data?.map((organization) => organization.code) ?? []}
          onDone={create.close}
        />
      </Modal>
    </Page>
  );
}
